import type { ColoringTemplate } from '@/contracts/content';

/**
 * Blank papers (spec §0.1 F12 — creative extensions).
 *
 * A colouring template is line art plus authored regions. A blank paper is the
 * same document with neither: no regions to fill, no outline to protect, just a
 * page. That makes free drawing a CONTENT decision rather than a second engine
 * — the same canvas, the same eight media, the same history, the same saves,
 * the same exports.
 *
 * A paper is a template with a permanent id, so an artwork made on one reopens
 * years later exactly as it was stored (§9.4). Their ids are namespaced
 * `paper_` so nothing ever confuses a blank page with a story's picture.
 */

function paper(id: string, title: string, width: number, height: number): ColoringTemplate {
  return {
    id,
    revision: 1,
    title,
    width,
    height,
    // No authored regions: there is nothing to fill and nothing to stay inside.
    regions: [],
    // No protected line art either — on a blank page every mark is the child's,
    // so the eraser can reach all of it.
    outline: '',
    livingSceneId: null,
    // A drawing of your own can go anywhere in the room a picture can go.
    worldSlots: ['wall-frame', 'shelf', 'window', 'garden'],
  };
}

export const PAPERS: ColoringTemplate[] = [
  paper('paper_landscape', 'Wide paper', 1800, 1200),
  paper('paper_portrait', 'Tall paper', 1200, 1800),
  paper('paper_square', 'Square paper', 1500, 1500),
];

export function getPaper(id: string): ColoringTemplate | undefined {
  return PAPERS.find((p) => p.id === id);
}

export function isPaper(templateId: string | null | undefined): boolean {
  return !!templateId && templateId.startsWith('paper_');
}
